import React from 'react';
import { useForm } from 'react-hook-form';
import DraggableDialog from '@components-clean/core/draggable-dialog';
import { TextAreaController } from '@components-clean/core/text-area';
import style from '@pages-clean/central-monitoring/monitoring-toolkit/tabs/dialogs/style.module.scss';
import { useTranslation } from '@hooks/index';
import { SubjectReviewInstanceApi } from '@apis/bff-api/subject-monitoring/subject-review-instance';

type RemoveFromReviewDialogProps = {
    datasetId: number;
    studyRef: string;
    subjectId: number;
    reviewRecordId: number;
    sessionId: string;
    onClose: () => void;
    onSuccess: () => void;
    onError: () => void;
};

interface IRemoveFromReviewForm {
    reason: string;
}

const RemoveFromReviewDialog = ({
    datasetId,
    studyRef,
    subjectId,
    reviewRecordId,
    sessionId,
    onClose,
    onSuccess,
    onError,
}: RemoveFromReviewDialogProps) => {
    const { t } = useTranslation('sm', { keyPrefix: 'subjectHub.removeFromReviewDialog' });

    const { trigger: removeFromReview, isMutating: isSaving } = SubjectReviewInstanceApi.useRemoveFromReview(studyRef);

    const {
        control,
        handleSubmit,
        formState: { isDirty, isValid, isSubmitting },
    } = useForm<IRemoveFromReviewForm>({
        mode: 'all',
        defaultValues: { reason: '' },
    });

    const onSubmitHandler = () =>
        handleSubmit(async (formData) => {
            const success = await removeFromReview({
                datasetId,
                reviewRecordId,
                reason: formData.reason,
                subjectIds: [subjectId],
                sessionId,
            });

            if (!success) {
                onError();
            } else {
                onSuccess();
            }
        })();

    return (
        <DraggableDialog
            dataTestId="remove-from-review-dialog"
            onClose={onClose}
            onSubmit={onSubmitHandler}
            title={t('title')}
            primaryBtnDisabled={!isDirty || !isValid || isSubmitting || isSaving}
            primaryBtnLabel={t('confirmButton')}
            secondaryBtnDisabled={isSubmitting || isSaving}
            height={230}
            initialWidth={530}
            bodyPadding="none"
        >
            <form className="k-h-full k-w-full k-d-flex k-flex-column k-gap-2" style={{ padding: '8px 11px' }}>
                <TextAreaController
                    control={control}
                    name="reason"
                    data-testid="remove-from-review-reason"
                    label={t('reasonLabel')}
                    required
                    maxLength={3000}
                    className={style.textAreaOverrides}
                    readOnly={isSubmitting}
                />
            </form>
        </DraggableDialog>
    );
};

export default RemoveFromReviewDialog;
